import { useEffect, useMemo, useRef, useState } from "react";
import { paymentMethodLabel } from "@/lib/paymentMethods";
import { useSearchParams } from "react-router-dom";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { PlaidBankSync } from "@/components/PlaidBankSync";
import { SubscriptionWatchlist } from "@/components/SubscriptionWatchlist";
import { SavingsTracker } from "@/components/SavingsTracker";
import { NetMovementChip } from "@/components/NetMovementChip";
import { ProcessorPayouts } from "@/components/ProcessorPayouts";
import {
  Plus, TrendUp, TrendDown, Sparkle, Trash, CircleNotch,
  ArrowUp, ArrowDown, ArrowRight, Wallet, Warning, UploadSimple, Question, X, Info, LockSimple, CheckCircle, ShieldCheck, FilePdf, Copy,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfidenceBadge, ConfidenceIndicator } from "@/components/ConfidenceBadge";
import { QuickBooksImport } from "@/components/QuickBooksImport";
import { DuplicateCompareDialog } from "@/components/DuplicateCompareDialog";
import { RowanTerm } from "@/components/RowanTerm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const celebrateFirstTxn = () => {
  const fire = (ratio, opts) =>
    confetti({ ...opts, origin: { y: 0.7 }, particleCount: Math.floor(200 * ratio), zIndex: 9999,
      colors: ["#4F46E5", "#16A34A", "#F0B429", "#D96C4A"] });
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
};

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-US", { month: "short" });
};

function SummaryCard({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="bk-card p-5 bk-lift" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon size={18} weight="duotone" color={color} />
        </span>
        <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>{label}</span>
      </div>
      <p className="font-display text-2xl font-bold" style={{ color }}>{money(value)}</p>
    </div>
  );
}

function TrendChart({ trend }) {
  const max = Math.max(1, ...trend.flatMap((t) => [t.income, t.expense]));
  return (
    <div className="bk-card p-6" data-testid="trend-chart">
      <h3 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>Last 6 months</h3>
      <div className="flex items-end justify-between gap-3 h-40">
        {trend.map((t) => (
          <div key={t.month} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end" data-testid={`trend-${t.month}`}>
            <div className="w-full flex items-end justify-center gap-1 h-full">
              <div className="w-1/2 rounded-t" style={{ height: `${(t.income / max) * 100}%`, background: "var(--bk-success)", minHeight: t.income > 0 ? 3 : 0 }} title={`Income ${money(t.income)}`} />
              <div className="w-1/2 rounded-t" style={{ height: `${(t.expense / max) * 100}%`, background: "#D96C4A", minHeight: t.expense > 0 ? 3 : 0 }} title={`Expenses ${money(t.expense)}`} />
            </div>
            <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{monthLabel(t.month)}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-4 text-xs" style={{ color: "var(--bk-text-2)" }}>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--bk-success)" }} /> Income</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "#D96C4A" }} /> Expenses</span>
      </div>
    </div>
  );
}

function CategoryBreakdown({ title, rows, color }) {
  const total = rows.reduce((a, r) => a + r.amount, 0);
  const max = Math.max(1, ...rows.map((r) => r.amount));
  return (
    <div className="bk-card p-6" data-testid={`breakdown-${title.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{title}</h3>
        <span className="font-display text-xl font-bold" style={{ color }}>{money(total)}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Nothing here yet. Let&apos;s change that.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.category}>
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: "var(--bk-text)" }}>{r.category}</span>
                <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(r.amount)}</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bk-page)" }}>
                <div className="h-2 rounded-full" style={{ width: `${(r.amount / max) * 100}%`, background: color }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Books({ businessId, business, isAgriculture = false, categoryFilter = "", focusTransactionId = "", permissions = {} }) {
  const token = localStorage.getItem("bk_token");
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState({ income: [], expense: [], accounts: [] });
  const [closedPeriods, setClosedPeriods] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [pnlRange, setPnlRange] = useState({ start: "", end: "" });
  const [coach, setCoach] = useState(null);
  const [coaching, setCoaching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [lockError, setLockError] = useState("");
  const [, setSearchParams] = useSearchParams();
  const [importing, setImporting] = useState(false);
  const [csvHelpOpen, setCsvHelpOpen] = useState(false);
  const [qbOpen, setQbOpen] = useState(false);
  const [qbActive, setQbActive] = useState(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("qb") === "open") setQbOpen(true);
  }, []);
  const csvRef = useRef(null);
  const [form, setForm] = useState({ date: today(), type: "expense", description: "", amount: "", category: "", account: "Bank", notes: "", location_tag: "" });
  const [jobs, setJobs] = useState([]);
  const [formJob, setFormJob] = useState("");
  const selectedClosedPeriod = closedPeriods.find((p) => p.period_key === (form.date || "").slice(0, 7)) || null;
  const [anomalies, setAnomalies] = useState([]);
  const [anomalySettings, setAnomalySettings] = useState({ enabled: true, large_transaction_threshold: 500, backfill_90d_completed_at: "" });
  const [savingAnomalySettings, setSavingAnomalySettings] = useState(false);
  const [anomalyBusy, setAnomalyBusy] = useState("");
  const [compareAnomaly, setCompareAnomaly] = useState(null);
  const [detailTxn, setDetailTxn] = useState(null);

  const loadTxns = () => api.get(`/businesses/${businessId}/transactions`).then((r) => setTxns(r.data)).catch(() => {});
  const loadClosedPeriods = () => api.get(`/businesses/${businessId}/closed-periods`).then((r) => setClosedPeriods(r.data.periods || [])).catch(() => {});
  const loadPnl = () => {
    const params = new URLSearchParams({ months: "6" });
    if (pnlRange.start) params.set("start_date", pnlRange.start);
    if (pnlRange.end) params.set("end_date", pnlRange.end);
    return api.get(`/businesses/${businessId}/reports/pnl?${params.toString()}`).then((r) => setPnl(r.data)).catch(() => {});
  };
  const loadAnomalies = () => api.get(`/anomalies?business_id=${businessId}&limit=50`).then((r) => setAnomalies(r.data)).catch(() => setAnomalies([]));
  const loadAnomalySettings = () => api.get(`/businesses/${businessId}/anomaly-settings`).then((r) => setAnomalySettings(r.data)).catch(() => {});
  const canEditBooks = permissions?.can_edit_books !== false;
  const refreshLedger = () => {
    loadTxns();
    loadClosedPeriods();
    loadPnl();
    loadAnomalies();
  };

  const loadQbActive = () => api.get(`/businesses/${businessId}/migration/quickbooks/sessions/active`).then((r) => setQbActive(r.data.active ? r.data.session : null)).catch(() => setQbActive(null));

  useEffect(() => {
    api.get(`/businesses/${businessId}/categories`).then((r) => setCats(r.data)).catch(() => {});
    api.get(`/businesses/${businessId}/jobs`).then((r) => setJobs(r.data)).catch(() => {});
    refreshLedger();
    loadAnomalySettings();
    loadQbActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    loadPnl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnlRange.start, pnlRange.end]);

  const addTxn = async () => {
    if (!form.description.trim()) return toast.error("Add a description");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter an amount");
    const isFirstReal = !txns.some((t) => t.source !== "sample");
    setSaving(true);
    setLockError("");
    try {
      const { data } = await api.post(`/businesses/${businessId}/transactions`, {
        date: form.date, type: form.type, description: form.description,
        amount: Number(form.amount), category: form.category, account: form.account, notes: form.notes,
        job_id: formJob || null, location_tag: form.location_tag,
      });
      if (isFirstReal) {
        celebrateFirstTxn();
        toast.success("🎉 Your first transaction is on the books!", { description: "This is where clean books begin. Keep 'em coming." });
      } else {
        toast.success(form.category ? "Transaction added" : (data?.category_source === "user_preference" ? "Added — Rowan used your saved preference" : "Added — AI categorized it for you"));
      }
      setForm({ date: today(), type: "expense", description: "", amount: "", category: "", account: "Bank", notes: "", location_tag: form.location_tag });
      setFormJob("");
      setShowForm(false);
      refreshLedger();
    } catch (err) {
      if (err.response?.status === 423) {
        const month = new Date(`${form.date}T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
        const msg = `${month} is closed. Reopen the month before adding or changing transactions.`;
        setLockError(msg);
        toast.error(msg);
      } else {
        toast.error(err.response?.data?.detail || "Couldn't save that transaction.");
      }
    } finally {
      setSaving(false);
    }
  };

  const setCategory = async (t, category) => {
    try {
      const { data } = await api.patch(`/transactions/${t.id}`, { category });
      if (data?.categorization_learning?.message) {
        toast.success(data.categorization_learning.message);
      }
      refreshLedger();
    } catch { toast.error("Couldn't update category."); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      setTxns((xs) => xs.filter((x) => x.id !== id));
      refreshLedger();
    } catch {}
  };

  const clearSamples = async () => {
    const samples = txns.filter((t) => t.source === "sample");
    if (samples.length === 0) return;
    try {
      await Promise.all(samples.map((t) => api.delete(`/transactions/${t.id}`)));
      setTxns((xs) => xs.filter((x) => x.source !== "sample"));
      toast.success("Sample entries cleared");
      refreshLedger();
    } catch { toast.error("Couldn't clear the samples."); }
  };

  const hasSamples = txns.some((t) => t.source === "sample");

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isFirstReal = !txns.some((t) => t.source !== "sample");
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/businesses/${businessId}/transactions/import-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rev = data.needs_review ? ` · ${data.needs_review} to review` : "";
      toast.success(`Imported ${data.imported} transaction${data.imported === 1 ? "" : "s"}${rev}`);
      if (isFirstReal && data.imported > 0) celebrateFirstTxn();
      refreshLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't import that CSV.");
    } finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const getCoach = async () => {    setCoaching(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/coach`);
      setCoach(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate your summary.");
    } finally {
      setCoaching(false);
    }
  };

  const catOptions = form.type === "income" ? cats.income : cats.expense;
  const jobMap = Object.fromEntries(jobs.map((j) => [j.id, j.name]));
  const [periodSel, setPeriodSel] = useState("this_month");
  const [customRange, setCustomRange] = useState({ start: today(), end: today() });
  const period = useMemo(() => {
    const now = new Date();
    const pad2 = (n) => String(n).padStart(2, "0");
    const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    if (periodSel === "ytd") {
      const label = now.getMonth() === 0
        ? `Jan ${now.getFullYear()}`
        : `Jan–${now.toLocaleString("en-US", { month: "short" })} ${now.getFullYear()}`;
      return { type: "ytd", start: `${now.getFullYear()}-01-01`, end: iso(new Date(now.getFullYear(), now.getMonth() + 1, 1)), label };
    }
    if (periodSel === "custom") {
      const { start: s, end: e } = customRange;
      if (!s || !e || e < s) return null;
      const sd = new Date(s + "T00:00:00"), ed = new Date(e + "T00:00:00");
      const endEx = new Date(ed); endEx.setDate(endEx.getDate() + 1);
      const label = s.slice(0, 7) === e.slice(0, 7)
        ? `${sd.toLocaleString("en-US", { month: "short" })} ${sd.getDate()}–${ed.getDate()}`
        : `${sd.toLocaleString("en-US", { month: "short", day: "numeric" })} – ${ed.toLocaleString("en-US", { month: "short", day: "numeric" })}`;
      return { type: "custom", start: s, end: iso(endEx), label };
    }
    if (/^\d{4}-\d{2}$/.test(periodSel)) {
      const [y, m] = periodSel.split("-").map(Number);
      return {
        type: "month", start: `${periodSel}-01`, end: iso(new Date(y, m, 1)),
        label: new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" }),
      };
    }
    return { type: "month", start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(new Date(now.getFullYear(), now.getMonth() + 1, 1)), label: "this month" };
  }, [periodSel, customRange]);
  const monthOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (i + 1), 1);
      return {
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleString("en-US", { month: "long", year: "numeric" }),
      };
    });
  }, []);
  const [hideVoided, setHideVoided] = useState(() => localStorage.getItem("ironroot-hide-voided") === "1");
  const toggleHideVoided = () => setHideVoided((v) => { localStorage.setItem("ironroot-hide-voided", v ? "0" : "1"); return !v; });
  const visibleTxns = useMemo(() => {
    let list = categoryFilter ? txns.filter((t) => t.category === categoryFilter) : txns;
    if (period) list = list.filter((t) => t.id === focusTransactionId || ((t.date || "") >= period.start && (t.date || "") < period.end));
    if (hideVoided) list = list.filter((t) => t.id === focusTransactionId || !t.voided);
    return list;
  }, [txns, categoryFilter, period, focusTransactionId, hideVoided]);
  const voidedInPeriod = useMemo(() => {
    let list = categoryFilter ? txns.filter((t) => t.category === categoryFilter) : txns;
    if (period) list = list.filter((t) => (t.date || "") >= period.start && (t.date || "") < period.end);
    return list.filter((t) => t.voided).length;
  }, [txns, categoryFilter, period]);

  useEffect(() => {
    if (!focusTransactionId || !visibleTxns.some((t) => t.id === focusTransactionId)) return;
    const row = document.querySelector(`[data-testid="txn-${focusTransactionId}"]`);
    if (!row) return;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    const prevOutline = row.style.outline;
    const prevBoxShadow = row.style.boxShadow;
    row.style.outline = "2px solid rgba(240, 180, 41, 0.9)";
    row.animate([
      { boxShadow: "0 0 0 0 rgba(240, 180, 41, 0.45)", transform: "scale(1)" },
      { boxShadow: "0 0 0 14px rgba(240, 180, 41, 0.08)", transform: "scale(1.01)" },
      { boxShadow: "0 0 0 0 rgba(240, 180, 41, 0)", transform: "scale(1)" },
    ], { duration: 1500, iterations: 2 });
    const timer = setTimeout(() => {
      row.style.outline = prevOutline;
      row.style.boxShadow = prevBoxShadow;
    }, 3200);
    return () => clearTimeout(timer);
  }, [focusTransactionId, visibleTxns]);
  const anomalyMap = useMemo(() => {
    const map = {};
    anomalies.forEach((item) => {
      if (!item.transaction_id) return;
      map[item.transaction_id] = [...(map[item.transaction_id] || []), item];
    });
    return map;
  }, [anomalies]);
  const openAnomalyCount = anomalies.length;
  const highAnomalyCount = anomalies.filter((item) => item.severity === "high").length;

  const saveAnomalySettings = async () => {
    setSavingAnomalySettings(true);
    try {
      const { data } = await api.patch(`/businesses/${businessId}/anomaly-settings`, {
        enabled: anomalySettings.enabled,
        large_transaction_threshold: Number(anomalySettings.large_transaction_threshold) || 500,
      });
      setAnomalySettings(data);
      toast.success(data.enabled ? "Anomaly settings saved" : "Anomaly monitoring paused for this business");
      refreshLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save anomaly settings.");
    } finally {
      setSavingAnomalySettings(false);
    }
  };

  const actOnAnomaly = async (id, action) => {
    setAnomalyBusy(`${action}-${id}`);
    try {
      await api.post(`/anomalies/${id}/${action}`);
      toast.success(action === "dismiss" ? "Rowan learned from that pattern." : "Flagged for review.");
      loadAnomalies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update that anomaly.");
    } finally {
      setAnomalyBusy("");
    }
  };

  const [voidBusy, setVoidBusy] = useState("");
  const voidTxn = async (id, { unvoid = false, reason = "" } = {}) => {
    setVoidBusy(id);
    try {
      await api.post(`/transactions/${id}/${unvoid ? "unvoid" : "void"}`, unvoid ? {} : { reason });
      toast.success(unvoid ? "Transaction restored to your books." : "Transaction voided — kept for the record, excluded from your books.");
      refreshLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update that transaction.");
    } finally {
      setVoidBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="books-ledger">
      {/* sample data banner */}
      {hasSamples && (
        <div data-testid="sample-data-banner" className="rounded-2xl border px-4 py-3 flex items-center gap-3 flex-wrap" style={{ background: "var(--bk-rowan-bg)", borderColor: "rgba(232,168,56,0.26)" }}>
          <Info size={18} weight="duotone" color="var(--bk-accent)" className="shrink-0" />
          <span className="text-sm flex-1 min-w-[200px]" style={{ color: "var(--bk-text-2)" }}>
            These are <span className="font-semibold" style={{ color: "var(--bk-text)" }}>sample entries</span> to show how your books look. Delete them whenever you're ready to start fresh.
          </span>
          <button onClick={clearSamples} disabled={!canEditBooks} data-testid="clear-samples-btn" className="text-sm font-semibold px-3 py-1.5 rounded-md border shrink-0 transition-colors hover:bg-white disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
            Clear samples
          </button>
        </div>
      )}

      {!canEditBooks && (
        <div className="rounded-2xl border px-4 py-3 text-sm" style={{ background: "#fff", borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="books-read-only-note">
          Read-only mode: you can review the ledger, but only owners and partners can add, edit, or delete transactions here.
        </div>
      )}

      {/* summary */}
      {pnl && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard label={`Income${pnlRange.start || pnlRange.end ? " · P&L range" : " · all time"}`} value={pnl.total_income} color="var(--bk-success)" icon={TrendUp} testid="summary-income" />
          <SummaryCard label={`Expenses${pnlRange.start || pnlRange.end ? " · P&L range" : " · all time"}`} value={pnl.total_expense} color="#D96C4A" icon={TrendDown} testid="summary-expenses" />
          <SummaryCard label={`Net Profit${pnlRange.start || pnlRange.end ? " · P&L range" : " · all time"}`} value={pnl.net_profit} color={pnl.net_profit >= 0 ? "var(--bk-accent)" : "#B23B1F"} icon={Wallet} testid="summary-net" />
        </div>
      )}

      <div className="bk-rowan-card p-6" data-testid="books-anomaly-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
              <Warning size={20} weight="duotone" color="#B23B1F" /> Rowan&apos;s anomaly watch
            </h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
              Rowan checks every new transaction for duplicates, odd timing, vendor spikes, personal-looking spend, and category surges.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: openAnomalyCount > 0 ? "#FDECE8" : "var(--bk-success-bg)", color: openAnomalyCount > 0 ? "#B23B1F" : "var(--bk-success)" }} data-testid="books-anomaly-count-pill">
              <Warning size={12} weight="fill" /> {openAnomalyCount} open
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "#FFF4D9", color: "#8A6A3B" }} data-testid="books-anomaly-high-pill">
              {highAnomalyCount} high priority
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-4 mt-5">
          <div className="space-y-3" data-testid="books-anomaly-list">
            {anomalies.length === 0 ? (
              <div className="rounded-[22px] border p-4 bg-white/85" style={{ borderColor: "rgba(91,138,111,0.24)" }} data-testid="books-anomaly-empty">
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>All clear right now.</p>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>If Rowan spots duplicate charges or strange spend patterns, they&apos;ll show up here and on the dashboard.</p>
              </div>
            ) : anomalies.slice(0, 4).map((item) => {
              const dismissBusy = anomalyBusy === `dismiss-${item.id}`;
              const reviewBusy = anomalyBusy === `review-${item.id}`;
              return (
                <div key={item.id} className="rounded-[22px] border p-4 bg-white/92" style={{ borderColor: item.severity === "high" ? "rgba(217,108,74,0.28)" : "rgba(232,168,56,0.24)" }} data-testid={`books-anomaly-item-${item.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{item.title}</p>
                    <span className="text-[11px] font-bold px-2 py-1 rounded-full" style={{ background: item.severity === "high" ? "#FDECE8" : "#FFF4D9", color: item.severity === "high" ? "#B23B1F" : "#8A6A3B" }}>{item.severity}</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{item.transaction_description} · {money(item.transaction_amount)} · {item.transaction_date}</p>
                  <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>{item.detail}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {item.anomaly_type === "duplicate_charge" && (
                      <button onClick={() => setCompareAnomaly(item)} disabled={dismissBusy || reviewBusy} data-testid={`books-anomaly-compare-${item.id}`} className="px-3 py-2 rounded-full text-xs font-bold text-white disabled:opacity-60 inline-flex items-center gap-1" style={{ background: "#B23B1F" }}>
                        <Copy size={12} weight="bold" /> See both charges
                      </button>
                    )}
                    <button onClick={() => actOnAnomaly(item.id, "dismiss")} disabled={!canEditBooks || dismissBusy || reviewBusy} data-testid={`books-anomaly-dismiss-${item.id}`} className="px-3 py-2 rounded-full text-xs font-semibold border disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                      {dismissBusy ? <span className="inline-flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Saving…</span> : <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Looks fine</span>}
                    </button>
                    <button onClick={() => actOnAnomaly(item.id, "review")} disabled={!canEditBooks || dismissBusy || reviewBusy} data-testid={`books-anomaly-review-${item.id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                      {reviewBusy ? <><CircleNotch size={12} className="animate-spin" /> Saving…</> : <><ShieldCheck size={12} weight="fill" /> Flag for review</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-[22px] border p-4 bg-white/92 h-fit" style={{ borderColor: "var(--bk-border)" }} data-testid="books-anomaly-settings-card">
            <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Detection settings</p>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Large-transaction alerts only fire when a charge clears this threshold and is 3× your trailing 90-day average.</p>
            <label className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 mt-4" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid="books-anomaly-enabled-toggle">
              <span>
                <span className="block text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Anomaly detection</span>
                <span className="block text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>Watch new transactions and keep learning from dismissals.</span>
              </span>
              <input type="checkbox" checked={!!anomalySettings.enabled} onChange={(e) => setAnomalySettings((s) => ({ ...s, enabled: e.target.checked }))} data-testid="books-anomaly-enabled-checkbox" />
            </label>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Large transaction threshold ($)</label>
              <input type="number" min="100" value={anomalySettings.large_transaction_threshold || 500} onChange={(e) => setAnomalySettings((s) => ({ ...s, large_transaction_threshold: e.target.value }))} disabled={!anomalySettings.enabled} data-testid="books-anomaly-threshold-input" className="w-full rounded-md border px-3 py-2 mt-1.5 text-sm outline-none disabled:opacity-60" style={{ borderColor: "var(--bk-border)" }} />
            </div>
            <button onClick={saveAnomalySettings} disabled={!canEditBooks || savingAnomalySettings} data-testid="books-anomaly-save-settings" className="bk-btn-primary mt-4 px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              {savingAnomalySettings ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : "Save anomaly settings"}
            </button>
          </div>
        </div>
      </div>

      <PlaidBankSync
        businessId={businessId}
        businessName={business?.name || ""}
        canEditBooks={canEditBooks}
        onTransactionsImported={refreshLedger}
      />

      <DuplicateCompareDialog
        anomaly={compareAnomaly}
        onClose={() => setCompareAnomaly(null)}
        onResolved={() => { loadAnomalies(); refreshLedger(); }}
      />

      {qbActive && !qbOpen && (
        <div className="rounded-2xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "rgba(232,168,56,0.4)", background: "var(--bk-rowan-bg)" }} data-testid="qb-resume-banner">
          <p className="text-sm font-medium" style={{ color: "var(--bk-text)" }}>
            {qbActive.status === "processing"
              ? "Rowan is still reading your QuickBooks files — open the import to check progress."
              : <>You have a QuickBooks import in progress — <b>{(qbActive.flagged_count_total || 0)}</b> flagged item{(qbActive.flagged_count_total || 0) === 1 ? "" : "s"} still to review.</>}
          </p>
          <button onClick={() => setQbOpen(true)} className="bk-btn-primary px-4 py-2 text-sm font-semibold" data-testid="qb-resume-btn">Continue import</button>
        </div>
      )}

      <SubscriptionWatchlist businessId={businessId} canEditBooks={canEditBooks} />

      <SavingsTracker businessId={businessId} />

      {/* add transaction */}
      <div className="bk-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Your ledger</h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Record money in and out. Leave the category blank and AI picks it for you.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
            <input ref={csvRef} type="file" accept=".csv,text/csv" onChange={onCsv} className="hidden" data-testid="csv-file-input" />
            <button onClick={() => csvRef.current?.click()} disabled={!canEditBooks || importing} data-testid="import-csv-btn" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70" style={{ color: "var(--bk-text)" }}>
              {importing ? <><CircleNotch size={16} className="animate-spin" /> Reading CSV…</> : <><UploadSimple size={16} weight="bold" /> Import CSV</>}
            </button>
            <button onClick={() => setCsvHelpOpen(true)} data-testid="csv-help-link" className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "var(--bk-accent)" }}>
              <Question size={15} weight="bold" /> How do I get my CSV?
            </button>
            <button onClick={() => setQbOpen(true)} disabled={!canEditBooks} data-testid="import-quickbooks-btn" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70" style={{ color: "var(--bk-text)" }}>
              <UploadSimple size={16} weight="bold" /> Import from QuickBooks
            </button>
            <button onClick={() => setShowForm((s) => !s)} disabled={!canEditBooks} data-testid="toggle-add-txn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
              <Plus size={16} weight="bold" /> Add transaction
            </button>
          </div>
        </div>

        {selectedClosedPeriod && (
          <div role="status" data-testid="txn-locked-month-banner" className="mt-5 flex flex-wrap items-center gap-3 px-4 py-3 rounded-md" style={{ background: "#FCEEE8", color: "var(--bk-energy)" }}>
            <LockSimple size={18} weight="fill" />
            <p className="text-sm font-semibold flex-1 min-w-[240px]">{selectedClosedPeriod.period_label} is closed. Reopen the month before adding, editing, categorizing, or deleting transactions.</p>
            <button onClick={() => setSearchParams({ tab: "close" })} data-testid="txn-locked-month-go-to-close" className="inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-2">
              Go to Month close <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        )}

        {showForm && (
          <div className="mt-5 pt-5 border-t space-y-4" style={{ borderColor: "var(--bk-border)" }} data-testid="add-txn-form">
            <div className="inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
              {["expense", "income"].map((t) => (
                <button key={t} onClick={() => setForm((f) => ({ ...f, type: t, category: "" }))} data-testid={`txn-type-${t}`}
                  className="px-4 py-2 text-sm font-semibold capitalize"
                  style={{ background: form.type === t ? (t === "income" ? "var(--bk-success)" : "#D96C4A") : "#fff", color: form.type === t ? "#fff" : "var(--bk-text-2)" }}>
                  {t}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DateInput value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="txn-date" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <input type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="txn-amount" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <input placeholder="Description (e.g. Client payment, Adobe subscription)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="txn-description" className="sm:col-span-2 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="txn-category" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                <option value="">✨ Auto-categorize with AI</option>
                {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.account} onChange={(e) => setForm((f) => ({ ...f, account: e.target.value }))} data-testid="txn-account" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                {cats.accounts.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              {isAgriculture && (
                <input value={form.location_tag} onChange={(e) => setForm((f) => ({ ...f, location_tag: e.target.value }))} data-testid="txn-location-tag" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} placeholder="Field / ranch / location" list="ag-books-locations" />
              )}
              {jobs.length > 0 && (
                <select value={formJob} onChange={(e) => setFormJob(e.target.value)} data-testid="txn-job" className="sm:col-span-2 rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                  <option value="">No job (optional)</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>Job: {j.name}</option>)}
                </select>
              )}
            </div>
            <div className="flex justify-end">
              {lockError && (
                <p role="alert" data-testid="txn-lock-error" className="mr-auto inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md" style={{ background: "#FCEEE8", color: "var(--bk-energy)" }}>
                  <LockSimple size={16} weight="fill" /> {lockError}
                </p>
              )}
              <button onClick={addTxn} disabled={saving} data-testid="save-txn-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70">
                {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <>Save transaction</>}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* P&L report + trend + breakdown */}
      {pnl && (
        <div className="bk-card p-6" data-testid="pnl-report">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}><RowanTerm term="pnl">Profit &amp; Loss</RowanTerm></h3>
            <div className="flex flex-wrap items-end gap-2 min-w-0 max-w-full">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--bk-text-2)" }}>From</label>
                <DateInput data-testid="pnl-start-date" max={pnlRange.end || undefined} value={pnlRange.start} onChange={(e) => setPnlRange((r) => ({ ...r, start: e.target.value }))} className="px-2.5 py-1.5 rounded-md border text-xs bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--bk-text-2)" }}>To</label>
                <DateInput data-testid="pnl-end-date" min={pnlRange.start || undefined} value={pnlRange.end} onChange={(e) => setPnlRange((r) => ({ ...r, end: e.target.value }))} className="px-2.5 py-1.5 rounded-md border text-xs bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              </div>
              {(pnlRange.start || pnlRange.end) && (
                <button type="button" data-testid="pnl-clear-range" onClick={() => setPnlRange({ start: "", end: "" })} className="text-xs font-semibold px-2 py-1.5 hover:underline" style={{ color: "var(--bk-accent)" }}>Clear</button>
              )}
            </div>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
            {[
              { label: "Income", value: pnl.total_income, testid: "pnl-income", color: "var(--bk-success)" },
              { label: "Cost of Goods Sold", term: "cogs", value: pnl.total_cogs || 0, testid: "pnl-cogs", color: "#B8860B" },
              { label: "Gross Profit", term: "gross_profit", value: pnl.gross_profit ?? pnl.total_income, testid: "pnl-gross-profit", bold: true },
              { label: "Operating Expenses", value: pnl.total_operating_expense ?? pnl.total_expense, testid: "pnl-expenses", color: "#D96C4A" },
              { label: "Net Income", value: pnl.net_profit, testid: "pnl-net-income", bold: true, color: pnl.net_profit >= 0 ? "var(--bk-accent)" : "#B23B1F" },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between py-2.5" data-testid={row.testid}>
                <span className={`text-sm ${row.bold ? "font-bold" : ""}`} style={{ color: "var(--bk-text)" }}>{row.term ? <RowanTerm term={row.term}>{row.label}</RowanTerm> : row.label}</span>
                <span className={`text-sm font-display ${row.bold ? "font-bold text-base" : "font-semibold"}`} style={{ color: row.color || "var(--bk-text)" }}>{money(row.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {pnl && pnl.transaction_count > 0 && (
        <>
          <TrendChart trend={pnl.monthly_trend} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CategoryBreakdown title="Income" rows={pnl.income_by_category} color="var(--bk-success)" />
            {(pnl.cogs_by_category || []).length > 0 && (
              <CategoryBreakdown title="COGS" rows={pnl.cogs_by_category} color="#B8860B" />
            )}
            <CategoryBreakdown title="Expenses" rows={pnl.operating_expense_by_category || pnl.expense_by_category} color="#D96C4A" />
          </div>
        </>
      )}

      {/* AI coach */}
      <div className="bk-rowan-card p-6" data-testid="ai-coach">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
              <Sparkle size={20} weight="fill" color="var(--bk-gold)" /> Rowan&apos;s bookkeeping note
            </h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>A plain-English review of your numbers with what to watch and what to do next.</p>
          </div>
          <button onClick={getCoach} disabled={coaching} data-testid="get-coach-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold shrink-0 disabled:opacity-70">
            {coaching ? <><CircleNotch size={16} className="animate-spin" /> Thinking…</> : <>{coach ? "Refresh" : "Get my review"}</>}
          </button>
        </div>
        {coach && (
          <div className="mt-5 space-y-4" data-testid="coach-result">
            <p className="font-display text-lg font-semibold" style={{ color: "var(--bk-text)" }}>{coach.headline}</p>
            {coach.highlights?.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--bk-success)" }}>Highlights</h4>
                <ul className="space-y-1.5">{coach.highlights.map((h, i) => <li key={i} className="flex gap-2 text-sm" style={{ color: "var(--bk-text)" }}><ArrowUp size={16} weight="bold" color="var(--bk-success)" className="shrink-0 mt-0.5" />{h}</li>)}</ul>
              </div>
            )}
            {coach.watch_outs?.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "#D96C4A" }}>Watch outs</h4>
                <ul className="space-y-1.5">{coach.watch_outs.map((w, i) => <li key={i} className="flex gap-2 text-sm" style={{ color: "var(--bk-text)" }}><ArrowDown size={16} weight="bold" color="#D96C4A" className="shrink-0 mt-0.5" />{w}</li>)}</ul>
              </div>
            )}
            {coach.actions?.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--bk-accent)" }}>Do next</h4>
                <ul className="space-y-1.5">{coach.actions.map((a, i) => <li key={i} className="flex gap-2 text-sm" style={{ color: "var(--bk-text)" }}><span className="font-mono text-xs mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{i + 1}</span>{a}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* card-processor payout matching (only shows when there are clearing items or past matches) */}
      <ProcessorPayouts businessId={businessId} onPosted={loadTxns} />

      {/* net movement chip (above the transaction timeline, synced with the ledger period filter) */}
      {period && <NetMovementChip businessId={businessId} period={period} refreshToken={txns} />}

      {/* transactions table */}
      <div className="bk-card p-0 overflow-hidden">
        <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Transactions</h3>
            <div className="flex items-center gap-2">
              {voidedInPeriod > 0 && (
                <button type="button" onClick={toggleHideVoided} aria-pressed={hideVoided} data-testid="ledger-hide-voided-toggle" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-xs font-semibold transition-colors" style={{ borderColor: hideVoided ? "var(--bk-accent)" : "var(--bk-border)", color: hideVoided ? "#fff" : "var(--bk-text-2)", background: hideVoided ? "var(--bk-accent)" : "#fff" }}>
                  {hideVoided ? `Showing ${voidedInPeriod} fewer` : `Hide voided (${voidedInPeriod})`}
                </button>
              )}
              <select value={periodSel} onChange={(e) => setPeriodSel(e.target.value)} data-testid="ledger-period-filter" className="rounded-md border px-2.5 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              <option value="this_month">This month</option>
              {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              <option value="ytd">Year to date</option>
              <option value="custom">Custom range</option>
            </select>
            </div>
          </div>
          {periodSel === "custom" && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="ledger-custom-range">
              <DateInput value={customRange.start} max={customRange.end || undefined} onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value }))} data-testid="ledger-custom-start" className="rounded-md border px-2.5 py-1.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>to</span>
              <DateInput value={customRange.end} min={customRange.start || undefined} onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value }))} data-testid="ledger-custom-end" className="rounded-md border px-2.5 py-1.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
            </div>
          )}
          {categoryFilter && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid="books-category-filter-pill">
              Watching category: {categoryFilter}
            </div>
          )}
          {focusTransactionId && visibleTxns.some((t) => t.id === focusTransactionId) && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "#FFF9ED", color: "#8A5A13" }} data-testid="books-focus-transaction-pill">
              Jumped to a likely problem transaction Rowan wants you to review
            </div>
          )}
          {closedPeriods.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="ledger-closed-periods">
              {closedPeriods.map((period) => (
                <span key={period.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "#F6F3ED", color: "#1a2744" }} data-testid={`ledger-closed-period-${period.period_key}`}>
                  <LockSimple size={12} weight="fill" color="#FF6B35" /> {period.period_label}
                </span>
              ))}
            </div>
          )}
        </div>
        {visibleTxns.length === 0 ? (
          <div className="p-10 text-center bk-empty-state m-6">
            <Wallet size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
            <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>{categoryFilter ? `No ${categoryFilter} transactions yet.` : txns.length > 0 ? "No transactions in this period." : "No transactions yet — the calm before the profit."}</p>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{categoryFilter ? "Try another category or add a new expense to compare your margin watchlist." : txns.length > 0 ? "Try a different period from the filter above, or add a transaction." : "Add one above, or confirm a scanned receipt — it flows in here automatically."}</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
            {visibleTxns.map((t) => {
              const isIncome = t.type === "income";
              const opts = isIncome ? cats.income : cats.expense;
              const rowAnomalies = anomalyMap[t.id] || [];
              const firstAnomaly = rowAnomalies[0];
              return (
                <div key={t.id} data-testid={`txn-${t.id}`} className="flex items-center gap-3 p-4 flex-wrap sm:flex-nowrap" style={{ borderColor: "var(--bk-border)", background: rowAnomalies.length ? "rgba(253,236,232,0.55)" : undefined }}>
                  <span className="text-xs font-mono w-20 shrink-0" style={{ color: "var(--bk-text-2)" }}>{t.date}</span>
                  {t.receipt_thumb_path && (
                    t.receipt_thumb_path.toLowerCase().endsWith(".pdf") ? (
                      <a href={`${BACKEND}/api/receipt-image/${t.receipt_thumb_path}?auth=${token}`} target="_blank" rel="noopener noreferrer" data-testid={`txn-receipt-pdf-${t.id}`} className="w-12 h-12 rounded border shrink-0 flex flex-col items-center justify-center gap-0.5" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)", color: "var(--bk-energy)" }}>
                        <FilePdf size={20} weight="duotone" />
                        <span className="text-[9px] font-bold leading-none">PDF</span>
                      </a>
                    ) : (
                      <img src={`${BACKEND}/api/receipt-image/${t.receipt_thumb_path}?auth=${token}`} alt={t.receipt_merchant || "Matched receipt"} data-testid={`txn-receipt-thumb-${t.id}`} className="w-12 h-12 rounded object-cover border shrink-0" style={{ borderColor: "var(--bk-border)" }} />
                    )
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                      <button type="button" onClick={() => setDetailTxn(t)} data-testid={`txn-open-detail-${t.id}`} className="truncate text-left hover:underline" style={{ color: "var(--bk-text)" }}>
                        {t.description}
                      </button>
                      {t.needs_review && <span className="inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FDECE8", color: "#B23B1F" }} data-testid={`review-${t.id}`}><Warning size={11} weight="fill" /> review</span>}
                      {rowAnomalies.length > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FDECE8", color: "#B23B1F" }} data-testid={`txn-anomaly-${t.id}`}><Warning size={11} weight="fill" /> {rowAnomalies.length} anomaly{rowAnomalies.length === 1 ? "" : "ies"}</span>}
                      {t.source === "receipt" && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>receipt</span>}
                      {t.source === "wellness_ledger" && <span data-testid={`wellness-pill-${t.id}`} className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-accent)" }}>{t.wellness_posting === "recognition" ? "earned · no cash" : t.wellness_posting === "forfeit" ? "forfeited · no cash" : (t.wellness_posting === "sale" ? "prepaid sale" : "package refund") + (t.payment_method ? ` · ${paymentMethodLabel(t.payment_method)}` : "")}</span>}
                      {t.source === "sample" && <span data-testid={`sample-pill-${t.id}`} className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "var(--bk-success-bg)", color: "var(--bk-accent)" }}>sample</span>}
                      {t.locked && <span data-testid={`txn-locked-${t.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#F6F3ED", color: "#1a2744" }}><LockSimple size={11} weight="fill" color="#FF6B35" /> {t.locked_period_label || "Closed"}</span>}
                      {t.voided && <span data-testid={`txn-voided-${t.id}`} className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>voided</span>}
                    </p>
                    <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{t.account}{t.location_tag ? ` · ${t.location_tag}` : ""}{t.job_id && jobMap[t.job_id] ? ` · ${jobMap[t.job_id]}` : ""}</p>
                    {t.receipt_thumb_path && <p className="text-[11px] mt-1" style={{ color: "var(--bk-accent)" }} data-testid={`txn-receipt-note-${t.id}`}>Receipt matched{t.receipt_match_status === "auto_matched" ? " automatically" : ""}{t.receipt_merchant ? ` · ${t.receipt_merchant}` : ""}</p>}
                    {(t.category_source === "user_preference" || t.category_source === "rowan") && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: t.category_source === "user_preference" ? "#F6F3ED" : "var(--bk-success-bg)", color: t.category_source === "user_preference" ? "#8A5A13" : "var(--bk-success)" }} data-testid={`txn-category-source-${t.id}`}>
                        <Info size={11} weight="fill" /> {t.category_source_label || (t.category_source === "user_preference" ? "Categorized based on your preference" : "Categorized by Rowan")}
                      </div>
                    )}
                    {firstAnomaly && (
                      <div className="mt-2" data-testid={`txn-anomaly-detail-${t.id}`}>
                        <p className="text-xs" style={{ color: "#7C2D12" }}>{firstAnomaly.detail}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <button onClick={() => actOnAnomaly(firstAnomaly.id, "dismiss")} disabled={!canEditBooks || anomalyBusy === `dismiss-${firstAnomaly.id}` || anomalyBusy === `review-${firstAnomaly.id}`} data-testid={`txn-anomaly-dismiss-${t.id}`} className="px-2.5 py-1.5 rounded-full text-[11px] font-semibold border disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                            {anomalyBusy === `dismiss-${firstAnomaly.id}` ? <span className="inline-flex items-center gap-1"><CircleNotch size={11} className="animate-spin" /> Saving…</span> : <span className="inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Looks fine</span>}
                          </button>
                          <button onClick={() => actOnAnomaly(firstAnomaly.id, "review")} disabled={!canEditBooks || anomalyBusy === `dismiss-${firstAnomaly.id}` || anomalyBusy === `review-${firstAnomaly.id}`} data-testid={`txn-anomaly-review-${t.id}`} className="px-2.5 py-1.5 rounded-full text-[11px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--bk-accent)" }}>
                            {anomalyBusy === `review-${firstAnomaly.id}` ? <span className="inline-flex items-center gap-1"><CircleNotch size={11} className="animate-spin" /> Saving…</span> : <span className="inline-flex items-center gap-1"><ShieldCheck size={11} weight="fill" /> Flag review</span>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {t.rowan_confidence && (
                    <ConfidenceBadge
                      confidence={t.rowan_confidence}
                      reason={t.rowan_reason}
                      category={t.category}
                      originalCategory={t.rowan_original_category}
                      categoryOptions={opts}
                      onSelectCategory={(!t.locked && canEditBooks) ? (c) => setCategory(t, c) : null}
                      hideLabel
                      allowBottomSheet
                      testId={`txn-confidence-${t.id}`}
                    />
                  )}
                  <select value={t.category} disabled={t.locked || !canEditBooks} onChange={(e) => setCategory(t, e.target.value)} data-testid={`txn-cat-${t.id}`} className="text-sm rounded-md border px-2.5 py-1.5 bg-white outline-none max-w-full min-w-0 sm:shrink-0 disabled:opacity-55" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    {opts.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <span className="text-sm font-bold w-24 text-right shrink-0" style={{ color: t.voided ? "var(--bk-text-2)" : isIncome ? "var(--bk-success)" : "#D96C4A", textDecoration: t.voided ? "line-through" : "none" }}>{isIncome ? "+" : "−"}{money(t.amount)}</span>
                  <button onClick={() => del(t.id)} disabled={t.locked || !canEditBooks} data-testid={`del-txn-${t.id}`} className="p-1.5 rounded hover:bg-black/5 shrink-0 disabled:opacity-40" style={{ color: "#B23B1F" }} aria-label="Delete"><Trash size={16} weight="bold" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!detailTxn} onOpenChange={(v) => { if (!v) setDetailTxn(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="txn-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--bk-text)" }} data-testid="txn-detail-title">Transaction detail</DialogTitle>
          </DialogHeader>
          {detailTxn && (() => {
            const t = txns.find((x) => x.id === detailTxn.id) || detailTxn;
            const isIncome = t.type === "income";
            const opts = isIncome ? cats.income : cats.expense;
            const corrected = t.rowan_original_category && t.category !== t.rowan_original_category;
            return (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Amount</p><p className="font-display text-xl font-bold mt-0.5" style={{ color: isIncome ? "var(--bk-success)" : "#D96C4A" }} data-testid="txn-detail-amount">{isIncome ? "+" : "−"}{money(t.amount)}</p></div>
                  <div><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Date</p><p className="mt-0.5" style={{ color: "var(--bk-text)" }} data-testid="txn-detail-date">{t.date}</p></div>
                  <div className="col-span-2"><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Payee</p><p className="mt-0.5" style={{ color: "var(--bk-text)" }} data-testid="txn-detail-payee">{t.description}</p></div>
                  <div><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Category</p><p className="mt-0.5 font-semibold" style={{ color: "var(--bk-text)" }} data-testid="txn-detail-category">{t.category}</p></div>
                  <div><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Account</p><p className="mt-0.5" style={{ color: "var(--bk-text)" }} data-testid="txn-detail-account">{t.account}</p></div>
                </div>
                {t.reversal_of_txn_id && !t.voided && (
                  <div className="rounded-2xl border p-4" style={{ borderColor: "rgba(232,168,56,0.4)", background: "#FFF4D9" }} data-testid="txn-detail-reversal-notice">
                    <p className="text-sm font-semibold" style={{ color: "#8A5A13" }}>Rowan flagged this as a reversal of an earlier transaction.</p>
                    {txns.some((x) => x.id === t.reversal_of_txn_id) ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <button type="button" onClick={() => setDetailTxn(txns.find((x) => x.id === t.reversal_of_txn_id))} data-testid="txn-detail-view-original" className="px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)", background: "#fff" }}>View original</button>
                        {canEditBooks && !(txns.find((x) => x.id === t.reversal_of_txn_id) || {}).voided && (
                          <button type="button" onClick={() => voidTxn(t.reversal_of_txn_id, { reason: `Reversed by the bank — reversal received ${t.date}` })} disabled={!!voidBusy} data-testid="txn-detail-void-original" className="px-3 py-1.5 rounded-full text-xs font-semibold text-white disabled:opacity-60" style={{ background: "#8A5A13" }}>
                            {voidBusy === t.reversal_of_txn_id ? "Voiding…" : "Void the original"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>The original transaction isn't in the current view — switch the period filter to find it.</p>
                    )}
                  </div>
                )}
                {t.voided ? (
                  <div className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid="txn-detail-voided-notice">
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Voided{t.voided_by ? ` by ${t.voided_by}` : ""}{t.voided_at ? ` on ${String(t.voided_at).slice(0, 10)}` : ""} — excluded from your books.</p>
                    {t.void_reason && <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="txn-detail-void-reason">Reason: {t.void_reason}</p>}
                    {canEditBooks && (
                      <button type="button" onClick={() => voidTxn(t.id, { unvoid: true })} disabled={!!voidBusy} data-testid="txn-detail-unvoid-btn" className="mt-2 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)", background: "#fff" }}>
                        {voidBusy === t.id ? "Restoring…" : "Unvoid — restore to books"}
                      </button>
                    )}
                  </div>
                ) : (!t.locked && canEditBooks && t.source !== "sample" && (
                  <button type="button" onClick={() => voidTxn(t.id)} disabled={!!voidBusy} data-testid="txn-detail-void-btn" className="self-start px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-60" style={{ borderColor: "rgba(178,59,31,0.35)", color: "#B23B1F", background: "#fff" }}>
                    {voidBusy === t.id ? "Voiding…" : "Void transaction"}
                  </button>
                ))}
                {t.rowan_confidence && (
                  <div className="rounded-2xl border p-4 mt-1" style={{ borderColor: "rgba(232,168,56,0.3)", background: "var(--bk-rowan-bg)" }} data-testid="txn-detail-rowans-take">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold flex items-center gap-1.5" style={{ color: "var(--bk-text)" }}>
                        <Sparkle size={15} weight="fill" color="var(--bk-gold)" /> Rowan&apos;s take
                      </p>
                      <ConfidenceIndicator confidence={t.rowan_confidence} testId="txn-detail-confidence" />
                    </div>
                    <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }} data-testid="txn-detail-rowan-reason">
                      {(t.rowan_reason || "").trim() || "Rowan categorized this transaction automatically."}
                    </p>
                    {corrected && (
                      <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="txn-detail-corrected-notice">You changed this to {t.category}.</p>
                    )}
                    {!t.locked && canEditBooks && (
                      <div className="mt-3">
                        <label className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Change category</label>
                        <select value={t.category} onChange={(e) => setCategory(t, e.target.value)} data-testid="txn-detail-change-category" className="mt-1 w-full rounded-md border px-2.5 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                          {opts.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={csvHelpOpen} onOpenChange={setCsvHelpOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="csv-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
              <UploadSimple size={20} weight="duotone" color="var(--bk-accent)" /> How to download your bank statement (CSV)
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
            Almost every bank lets you export transactions as a CSV. Here's the 3-step version that works for most:
          </p>
          <ol className="space-y-3 mt-1">
            {[
              { t: "Log in to your bank", d: "Open your bank's website or app and go to the account you want to reconcile." },
              { t: "Find Statements / Activity → Export", d: "Look for \"Statements\", \"Transactions\", \"Activity\", or \"Export\". Pick a date range, then choose Download / Export and select CSV (sometimes called \"comma-separated\" or \"spreadsheet\")." },
              { t: "Import it here", d: "Save the file, come back and click Import CSV. We auto-map the columns and AI categorizes every transaction — you just confirm." },
            ].map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`csv-help-step-${i + 1}`}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 text-white" style={{ background: "var(--bk-gradient-primary)" }}>{i + 1}</span>
                <span>
                  <span className="block font-semibold text-sm" style={{ color: "var(--bk-text)" }}>{s.t}</span>
                  <span className="block text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>{s.d}</span>
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs rounded-md p-3 mt-1" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>
            Chase, Bank of America, Wells Fargo, Amex and most others support CSV export. If you only see PDF, look under an "Export" or "Download" menu, or try the desktop site.
          </p>
        </DialogContent>
      </Dialog>
      {isAgriculture && (
        <datalist id="ag-books-locations">
          {(business?.agriculture_settings?.locations || []).map((loc) => <option key={loc} value={loc} />)}
        </datalist>
      )}
      <QuickBooksImport
        businessId={businessId}
        open={qbOpen}
        resumeSession={qbActive}
        onClose={() => { setQbOpen(false); loadQbActive(); }}
        onImported={() => { refreshLedger(); loadQbActive(); }}
      />
    </div>
  );
}
